import * as React from 'react'

import { InfoIcon, SquareArrowOutUpRightIcon } from 'lucide-react'
import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IAttachment } from '~/client/graph'
import ListTagCell, {
  ICellEntity,
  TagCellItemKeyEnum,
} from '~/client/src/desktop/components/ListTagCell'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import BaseEntityAssociationStatus from '~/client/src/shared/components/BaseEntityAssociationStatus/BaseEntityAssociationStatus'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import * as Icons from '~/client/src/shared/components/Icons'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import WorkflowBacklinks from '~/client/src/shared/components/WorkflowBacklinks/WorkflowBacklinks'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import SitePermitAssignmentsStore from '~/client/src/shared/stores/domain/SitePermitAssignments.store'
import SitePermitFollowingsStore from '~/client/src/shared/stores/domain/SitePermitFollowings.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { LogisticsDataKeys } from '~/client/src/shared/types/LogisticsDataKeys'
import { NOOP } from '~/client/src/shared/utils/noop'
import { BULLET, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import LogisticsStore from '../Logistics/Logistics.store'
import LogisticsListTable from '../LogisticsList/components/LogisticsListTable/LogisticsListTable'
import LogisticsTimelineCell from './components/LogisticsTimelineCell'
import LogisticsTimelineHeader from './components/LogisticsTimelineHeader'
import LogisticsGanttStore from './LogisticsGantt.store'

import './LogisticsGantt.scss'

interface IProps {
  logisticsStore: LogisticsStore
  store: LogisticsGanttStore

  workflowAssignmentsStore?: SitePermitAssignmentsStore
  workflowFollowingsStore?: SitePermitFollowingsStore
  projectDateStore?: ProjectDateStore
  projectsStore?: ProjectsStore
  sharedSitePermitStore?: SharedSitePermitStore
  workflowTypesStore?: WorkflowTypesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
}

const HEADER_COLUMN_CLASS_MAP = {
  [LogisticsDataKeys.LOGISTIC_END_DATE]: 'br-light-cool-grey',
  [LogisticsDataKeys.LOGISTICS_TIMELINE]:
    'pa0 br-light-cool-grey unclickable-element',
}
const CELL_COLUMN_CLASS_MAP = {
  [LogisticsDataKeys.LOGISTICS_TIMELINE]: 'pa0 bb-none',
}
const CATEGORY_COLUMN_CLASS_MAP = {
  [LogisticsDataKeys.LOGISTIC_END_DATE]: 'br-light-cool-grey',
  [LogisticsDataKeys.LOGISTICS_TIMELINE]:
    'pa0 br-light-cool-grey unclickable-element bb-none',
}
const DISABLED_RESIZE_COLUMNS = [
  LogisticsDataKeys.CHECKBOX,
  LogisticsDataKeys.LOGISTICS_TIMELINE,
]
const COLUMN_WITH_ICON_OFFSET = 48
const DEFAULT_COLUMN_ELLIPSIS_OFFSET = 0

@inject(
  'workflowAssignmentsStore',
  'workflowFollowingsStore',
  'projectDateStore',
  'projectsStore',
  'sharedSitePermitStore',
  'workflowTypesStore',
  'workflowCategoriesStore',
)
@observer
export default class LogisticsGantt extends React.Component<IProps> {
  @observable private logisticsStore: LogisticsStore = null

  public constructor(props: IProps) {
    super(props)

    this.logisticsStore = props.logisticsStore
    this.props.logisticsStore.changeDatePickerMode(
      TwoMonthsDatePickerMode.FIXED_WEEK,
    )

    makeObservable(this)
  }

  public componentWillUnmount(): void {
    this.props.store.resetSelection()
  }

  public render() {
    const {
      rows,
      columns,
      columnsWidthState,
      collapsedCategories,
      sortState,
      onRowClick,
      toggleListCategory,
      handleColumnSort,
      toggleCategoryCollapsing,
      selectAll,
      unselectAll,
      fixedColumnsCount,
    } = this.props.store

    return (
      <>
        {this.confirmSubscriptionDialog}
        {this.deletionConfirmDialog}
        <div className="relative row full-height logistics-gantt">
          <LogisticsListTable
            rows={rows}
            sortedColumnKey={sortState.columnKey}
            sortingOrder={sortState.order}
            columns={columns}
            columnsWidthState={columnsWidthState}
            collapsedCategories={collapsedCategories}
            columnKeyToCellRenderer={this.columnRenderer}
            columnKeyToHeaderRenderer={this.headerColumnRenderer}
            columnKeyToCategoryCellRenderer={this.categoryCellRenderer}
            columnHeaderClassesMap={HEADER_COLUMN_CLASS_MAP}
            columnCellClassesMap={CELL_COLUMN_CLASS_MAP}
            columnCategoryCellClassesMap={CATEGORY_COLUMN_CLASS_MAP}
            disabledResizeColumns={DISABLED_RESIZE_COLUMNS}
            fixedColumnsCount={fixedColumnsCount}
            onCategoryCollapsingToggle={toggleCategoryCollapsing}
            onCategoryCheckboxToggle={toggleListCategory}
            onCellClick={onRowClick}
            onColumnSort={handleColumnSort}
            selectAll={selectAll}
            resetAll={unselectAll}
          />
        </div>
      </>
    )
  }

  private get confirmSubscriptionDialog(): JSX.Element {
    const {
      shouldSubscriptionConfirmModalShow,
      closeSubscriptionConfirmModal,
      subscriptionConfirmMessage,
      handleChangeAssociations,
      subscriptionConfirmButtonText,
    } = this.props.store

    return (
      <ConfirmDialog
        isOpen={shouldSubscriptionConfirmModalShow}
        onCancelClicked={closeSubscriptionConfirmModal}
        onDoneClicked={handleChangeAssociations}
        doneTitle={subscriptionConfirmButtonText}
      >
        <div className="text large pre-line">{subscriptionConfirmMessage}</div>
      </ConfirmDialog>
    )
  }

  private get deletionConfirmDialog(): JSX.Element {
    const {
      isDeletionConfirmModalShown,
      closeDeletionConfirmModal,
      deletionConfirmMessage,
      deleteWorkflows,
      isUpdating,
    } = this.props.store

    return (
      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.doYouWantToDeleteSelectedWorkflows}
        isOpen={isDeletionConfirmModalShown}
        loading={isUpdating}
        onCancelClicked={isUpdating ? NOOP : closeDeletionConfirmModal}
        onDoneClicked={deleteWorkflows}
        doneTitle={`${Localization.translator.yes}, ${Localization.translator.delete}`}
      >
        <div className="text large pre-line tw:pb-2.5">
          {Localization.translator.logisticsListDescriptions.areYouWantToDelete}
          <br />
          <br />
          {BULLET} {deletionConfirmMessage}
        </div>
      </ConfirmDialog>
    )
  }

  private get headerColumnRenderer() {
    return {
      [LogisticsDataKeys.LOGISTICS_TIMELINE]: () => (
        <LogisticsTimelineHeader
          logisticsGanttViewStore={this.props.store}
          projectDateStore={this.props.projectDateStore}
        />
      ),
    }
  }

  private get categoryCellRenderer() {
    return {
      [LogisticsDataKeys.LOGISTICS_TIMELINE]: this.renderTimelineColumn,
    }
  }

  private get columnRenderer() {
    return {
      [LogisticsDataKeys.CHECKBOX]: value => <Checkbox checked={value} />,
      [LogisticsDataKeys.LOGISTIC_ID]: this.renderIdColumn,
      [LogisticsDataKeys.LOGISTIC_NAME]: this.renderNameColumn,
      [LogisticsDataKeys.LOGISTIC_TYPE]: this.renderTypeColumn,
      [LogisticsDataKeys.LOGISTIC_CATEGORY]: this.renderCategoryColumn,
      [LogisticsDataKeys.LOGISTIC_STATUS]: this.renderLogisticStatus,
      [LogisticsDataKeys.LOGISTIC_LOCATION]: this.renderLocationColumns,
      [LogisticsDataKeys.LOGISTIC_EQUIPMENT]: this.renderLocationColumns,
      [LogisticsDataKeys.LOGISTIC_COMPANY]: this.renderCompanyColumn,
      [LogisticsDataKeys.LOGISTIC_START_DATE]: this.renderDateColumn,
      [LogisticsDataKeys.LOGISTIC_END_DATE]: this.renderDateColumn,
      [LogisticsDataKeys.LOGISTIC_RESPONSIBLE]:
        this.renderResponsibleContactColumn,
      [LogisticsDataKeys.LOGISTIC_ATTACHMENT]: this.renderAttachmentColumns,
      [LogisticsDataKeys.LOGISTICS_TIMELINE]: this.renderTimelineColumn,
    }
  }

  private renderResponsibleContactColumn = (users: User[]): JSX.Element => (
    <ListTagCell items={users} renderItemKey={TagCellItemKeyEnum.USER} />
  )

  private renderNameColumn = (value: {
    id: string
    name: string
    workflow?: Workflow
  }): JSX.Element => {
    const { name, id, workflow } = value
    const title = name || NO_VALUE
    const isShared = this.isSharedEntity(id)
    return (
      <div className="row unset-flex-children-direct full-width">
        <div
          className={classList({
            'text-ellipsis': true,
            'text grey large': isShared,
          })}
          title={title}
        >
          {title}
        </div>
        {workflow && (
          <WorkflowBacklinks
            workflow={workflow}
            onWorkflowClick={this.logisticsStore.showWorkflowViewDialog}
          />
        )}
      </div>
    )
  }

  private renderCompanyColumn = (companies: Company[]): JSX.Element => (
    <ListTagCell items={companies} renderItemKey={TagCellItemKeyEnum.COMPANY} />
  )

  private renderIdColumn = (payload: {
    logisticId: string
    code: string
    isDraft: boolean
    isShared: boolean
  }): JSX.Element => {
    return (
      <div className="row full-width">
        {!payload.isDraft && !payload.isShared && (
          <BaseEntityAssociationStatus
            className="no-grow tw:ml-3 tw:mr-5"
            entityId={payload.logisticId}
            domainAssignmentsStore={this.props.workflowAssignmentsStore}
            domainFollowingsStore={this.props.workflowFollowingsStore}
          />
        )}
        {this.renderSharedEntityInfo(payload.logisticId)}
        {this.renderDraftIndicator(payload.isDraft)}
        {this.renderStringColumn(payload.code, payload.isShared)}
      </div>
    )
  }

  private isSharedEntity = (id: string): boolean => {
    return !!this.logisticsStore.getSharedFormById(id)
  }

  private renderSharedEntityInfo = (id: string): JSX.Element => {
    const { sharedSitePermitStore, projectsStore } = this.props
    const sharedForm = this.logisticsStore.getSharedFormById(id)
    if (!sharedForm) {
      return null
    }

    const form = Workflow.fromSharedPermitDto(sharedForm)

    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <div className="tw:flex tw:items-center no-grow tw:mx-2.5 bg-grey-40 tw:p-0.5 text white pointer" />
          }
        >
          <SquareArrowOutUpRightIcon className="tw:size-4" />
          {Localization.translator.shared}
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {Localization.translator.sharedFrom}
          <span className="tw:underline">
            {form.sharedFromToDisplay(sharedSitePermitStore, projectsStore)}
          </span>
        </TooltipContent>
      </Tooltip>
    )
  }

  private renderDraftIndicator = (isDraft: boolean): JSX.Element => {
    if (!isDraft) return null

    return (
      <div className="row no-grow tw:mx-2.5 bg-grey-40 tw:p-0.5 text white pointer">
        {Localization.translator.draft_noun}
      </div>
    )
  }

  private renderStringColumn = (
    value: string,
    isShared?: boolean,
  ): JSX.Element => {
    return (
      <div
        className={classList({
          'text-ellipsis': true,
          'text grey large': isShared,
        })}
        title={value || NO_VALUE}
      >
        {value || NO_VALUE}
      </div>
    )
  }

  private renderDateColumn = (payload: { date: number }): JSX.Element => {
    const { getWeekdayMonthDayYearAndTimeToDisplay } =
      this.props.projectDateStore
    const dateString = getWeekdayMonthDayYearAndTimeToDisplay(payload.date)

    return (
      <div className="row full-width">
        <div className="col y-center full-width">
          <div className="row text ellipsis" title={dateString}>
            {dateString || NO_VALUE}
          </div>
        </div>
      </div>
    )
  }

  private renderTypeColumn = (data: {
    type: string
    workflow: Workflow
  }): JSX.Element => {
    const { type, workflow } = data
    const { workflowTypesStore, store } = this.props

    const typeName = workflow.getNameOfPermitType(workflowTypesStore) || type

    return (
      <div className="row y-center full-height">
        {typeName && (
          <WorkflowTypeIcon
            iconName={workflow.getIconNameOfPermitType(workflowTypesStore)}
            size="small"
            className="tw:mr-1"
          />
        )}
        <div
          style={{
            maxWidth:
              store.columnsWidthState.get(LogisticsDataKeys.LOGISTIC_TYPE) -
              COLUMN_WITH_ICON_OFFSET,
            minWidth:
              store.columnsWidthState.get(LogisticsDataKeys.LOGISTIC_TYPE) -
              COLUMN_WITH_ICON_OFFSET,
          }}
        >
          <div className="text large ellipsis">{typeName || NO_VALUE}</div>
        </div>
      </div>
    )
  }

  private renderCategoryColumn = (data: {
    categoryName: string
    categoryId: string
    workflow?: Workflow
  }): JSX.Element => {
    const { categoryName, workflow } = data
    const { workflowCategoriesStore, store } = this.props

    const category = workflow?.categoryId
      ? workflow.getCategoryCaption(workflowCategoriesStore)
      : NO_VALUE
    if (workflow) {
      const isDefaultCategory = workflowCategoriesStore.getById(
        workflow.categoryId,
      )?.isDefaultCategory

      return (
        <div className="row text large">
          {isDefaultCategory && (
            <Icons.LogoDark className="row no-grow tw:mr-[5px] default-category-icon" />
          )}
          <div
            className="row"
            style={{
              maxWidth:
                store.columnsWidthState.get(
                  LogisticsDataKeys.LOGISTIC_CATEGORY,
                ) -
                (isDefaultCategory
                  ? COLUMN_WITH_ICON_OFFSET
                  : DEFAULT_COLUMN_ELLIPSIS_OFFSET),
              minWidth:
                store.columnsWidthState.get(
                  LogisticsDataKeys.LOGISTIC_CATEGORY,
                ) -
                (isDefaultCategory
                  ? COLUMN_WITH_ICON_OFFSET
                  : DEFAULT_COLUMN_ELLIPSIS_OFFSET),
            }}
          >
            <div className="text large ellipsis row">
              {category || categoryName || NO_VALUE}
              {isDefaultCategory && (
                <TooltipWrapper
                  content={Localization.translator.categoryCreatedByStruxHub}
                  className="tw:flex tw:items-center no-grow tw:ml-1"
                >
                  <InfoIcon className="tw:size-3 tw:shrink-0" />
                </TooltipWrapper>
              )}
            </div>
          </div>
        </div>
      )
    }

    return <div className="text large row y-center">-</div>
  }

  private renderLocationColumns = (payload: {
    isBreadcrumb: boolean
    locations: LocationBase[]
  }): JSX.Element => (
    <ListTagCell
      isBreadcrumb={payload?.isBreadcrumb}
      items={payload?.locations}
      renderItemKey={TagCellItemKeyEnum.LOCATION}
      className="full-width"
    />
  )

  private renderAttachmentColumns = (payload: {
    attachments: (IAttachment & ICellEntity)[]
  }): JSX.Element => (
    <ListTagCell
      isBreadcrumb={false}
      items={payload?.attachments}
      renderItemKey={TagCellItemKeyEnum.ATTACHMENT}
      className="full-width"
    />
  )

  private renderLogisticStatus = (payload: {
    status?: any
    workflowStepLevel?: number
    isLate?: boolean
  }) => (
    <div className="row no-flex-children full-width">
      <StatusBadge
        status={payload.status}
        workflowStepLevel={payload.workflowStepLevel}
        isLate={payload.isLate}
      />
    </div>
  )

  private renderTimelineColumn = (logisticItem: ILogisticItem): JSX.Element => {
    return (
      <LogisticsTimelineCell
        logisticItem={logisticItem}
        logisticsGanttStore={this.props.store}
      />
    )
  }
}
